import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { CurrentUserService } from '@services/auth/current-user.service';
import { PrimeNG } from 'primeng/config';

// PrimeNG locale definitions for calendar/datepicker
const PRIMENG_LOCALES: Record<string, any> = {
	es: {
		firstDayOfWeek: 1,
		dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
		dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
		dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
		monthNames: [
			'enero',
			'febrero',
			'marzo',
			'abril',
			'mayo',
			'junio',
			'julio',
			'agosto',
			'septiembre',
			'octubre',
			'noviembre',
			'diciembre',
		],
		monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
		today: 'Hoy',
		clear: 'Limpiar',
		weekHeader: 'Sm',
		dateFormat: 'dd/mm/yy',
	},
	en: {
		firstDayOfWeek: 0,
		dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
		dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
		dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
		monthNames: [
			'January',
			'February',
			'March',
			'April',
			'May',
			'June',
			'July',
			'August',
			'September',
			'October',
			'November',
			'December',
		],
		monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
		today: 'Today',
		clear: 'Clear',
		weekHeader: 'Wk',
		dateFormat: 'dd/mm/yy',
	},
	zh: {
		firstDayOfWeek: 1,
		dayNames: ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'],
		dayNamesShort: ['日', '一', '二', '三', '四', '五', '六'],
		dayNamesMin: ['日', '一', '二', '三', '四', '五', '六'],
		monthNames: [
			'一月',
			'二月',
			'三月',
			'四月',
			'五月',
			'六月',
			'七月',
			'八月',
			'九月',
			'十月',
			'十一月',
			'十二月',
		],
		monthNamesShort: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
		today: '今天',
		clear: '清除',
		weekHeader: '周',
		dateFormat: 'dd/mm/yy',
	},
	ca: {
		firstDayOfWeek: 1,
		dayNames: ['diumenge', 'dilluns', 'dimarts', 'dimecres', 'dijous', 'divendres', 'dissabte'],
		dayNamesShort: ['dg', 'dl', 'dt', 'dc', 'dj', 'dv', 'ds'],
		dayNamesMin: ['dg', 'dl', 'dt', 'dc', 'dj', 'dv', 'ds'],
		monthNames: [
			'gener',
			'febrer',
			'març',
			'abril',
			'maig',
			'juny',
			'juliol',
			'agost',
			'setembre',
			'octubre',
			'novembre',
			'desembre',
		],
		monthNamesShort: ['gen', 'feb', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'oct', 'nov', 'des'],
		today: 'Avui',
		clear: 'Netejar',
		weekHeader: 'Sm',
		dateFormat: 'dd/mm/yy',
	},
	fr: {
		firstDayOfWeek: 1,
		dayNames: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
		dayNamesShort: ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'],
		dayNamesMin: ['D', 'L', 'M', 'M', 'J', 'V', 'S'],
		monthNames: [
			'janvier',
			'février',
			'mars',
			'avril',
			'mai',
			'juin',
			'juillet',
			'août',
			'septembre',
			'octobre',
			'novembre',
			'décembre',
		],
		monthNamesShort: ['jan', 'fév', 'mar', 'avr', 'mai', 'jun', 'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'],
		today: "Aujourd'hui",
		clear: 'Effacer',
		weekHeader: 'Sm',
		dateFormat: 'dd/mm/yy',
	},
};

/**
 * Service responsible for initializing the application language during startup.
 * This runs as an APP_INITIALIZER before the application renders.
 */
@Injectable({
	providedIn: 'root',
})
export class LanguageInitService {
	// `zh` used to be listed here with an empty zh.json behind it, so picking it
	// (or inheriting it from a stored preference) rendered the whole UI through
	// the Spanish fallback under a Chinese flag.
	private readonly supportedLanguages = ['es', 'en'];
	// English by default: the platform's audience is the Gulf, and `users.
	// preferredLanguage` defaults to 'en' on the backend. The frontend still
	// said 'es', so every signed-out screen — login, company signup, password
	// reset — opened in Spanish regardless. PRIMENG_LOCALES carries 'en', so the
	// datepicker follows.
	private readonly defaultLanguage = 'en';
	private transloco = inject(TranslocoService);
	private currentUserService = inject(CurrentUserService);
	private primeNG = inject(PrimeNG);

	/**
	 * Initializes the application language.
	 * Called during APP_INITIALIZER phase.
	 * Loads the user's preferred language from localStorage if available.
	 */
	async initializeLanguage(): Promise<void> {
		let langToSet = this.defaultLanguage;

		const preferredLang = this.currentUserService.getPreferredLanguage();
		if (preferredLang && this.supportedLanguages.includes(preferredLang)) {
			langToSet = preferredLang;
		}

		// Set the language
		this.transloco.setActiveLang(langToSet);

		// Set PrimeNG locale for datepicker/calendar
		this.updatePrimeNGLocale(langToSet);

		// Subscribe to language changes to update PrimeNG locale
		this.transloco.langChanges$.subscribe((lang) => {
			this.updatePrimeNGLocale(lang);
		});

		// Wait for translations to be loaded
		await this.waitForTranslations(langToSet);
	}

	/**
	 * Update PrimeNG translation/locale settings
	 */
	private updatePrimeNGLocale(lang: string): void {
		const locale = PRIMENG_LOCALES[lang] || PRIMENG_LOCALES['en'];
		this.primeNG.setTranslation(locale);
	}

	/**
	 * Wait for translations to be loaded for a specific language
	 */
	private async waitForTranslations(lang: string): Promise<void> {
		return new Promise((resolve) => {
			// Check if translations are already loaded
			const existingTranslation = this.transloco.getTranslation(lang);
			if (existingTranslation && Object.keys(existingTranslation).length > 0) {
				resolve();
				return;
			}

			// Load the translations explicitly
			let subscription: { unsubscribe: () => void } | null = null;
			let resolved = false;

			const cleanup = () => {
				if (!resolved) {
					resolved = true;
					subscription?.unsubscribe();
					resolve();
				}
			};

			subscription = this.transloco.load(lang).subscribe({
				next: () => cleanup(),
				error: () => cleanup(),
			});

			// Safety timeout (3 seconds)
			setTimeout(cleanup, 3000);
		});
	}
}
